import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CalendarDays } from "lucide-react";

import ACL_RELATIONSHIPS from "acl-relationships";
import AccessDenied from "components/AccessDenied";
import SidebarDetailLayout from "components/SidebarDetailLayout";
import { DetailBreadcrumb } from "components/sidebar/DetailBreadcrumb";
import { useAppTabs } from "hooks/useAppTabs";
import useAuthorized from "hooks/useAuthorized";
import usePageTitle from "hooks/usePageTitle";
import NotFoundItem from "routes/404/NotFoundItem";
import EventDatasetButton from "routes/meta_datasets/swoogo_events/EventDatasetButton";
import useCanSetEventDataset from "routes/meta_datasets/swoogo_events/useCanSetEventDataset";
import EventOverview from "routes/swoogo/EventOverview";
import { RecordLoadError } from "routes/swoogo/overviewPrimitives";
import { swoogoEventKeys } from "routes/swoogo/queryKeys";
import useRecordTableSections from "routes/swoogo/useRecordTableSections";
import SwoogoService from "services/Swoogo";

const NOT_FOUND_STATUS = 404;

const EventDetail = () => {
  const { id } = useParams();
  const tabs = useAppTabs({
    basePath: `/swoogo/events/${id}`,
    defaultTab: "overview",
  });
  const eventId = Number(id);

  const canRead = useAuthorized([ACL_RELATIONSHIPS.swoogo.readEvents]);
  const canSetDataset = useCanSetEventDataset();
  const tableSections = useRecordTableSections(
    ["sessions", "registrants", "attendances"],
    "event_id",
    eventId
  );

  const {
    data: event,
    isLoading,
    error,
  } = useQuery({
    queryKey: swoogoEventKeys.detail(eventId),
    queryFn: () => SwoogoService.getEvent(eventId),
    enabled: canRead,
    retry: false,
  });

  usePageTitle(event?.name || `Event #${id}`);

  if (!canRead) {
    return (
      <AccessDenied description="You don't have permission to view this event." />
    );
  }

  if (error?.response?.status === NOT_FOUND_STATUS) {
    return <NotFoundItem />;
  }

  if (error) {
    return <RecordLoadError title="Could not load the event" />;
  }

  const sections = [
    {
      key: "overview",
      label: "Overview",
      icon: CalendarDays,
      content: <EventOverview event={event} isLoading={isLoading} />,
    },
    ...tableSections,
  ];

  return (
    <SidebarDetailLayout
      sections={sections}
      tabs={tabs}
      header="Event"
      breadcrumb={
        <DetailBreadcrumb
          parentTo="/swoogo/events"
          parentLabel="Events"
          current={event?.name || `Event #${id}`}
        />
      }
      title={event?.name || `Event #${id}`}
      titleActions={
        event && canSetDataset ? (
          <EventDatasetButton eventId={event.id} />
        ) : null
      }
    />
  );
};

export default EventDetail;
